import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import QuollWordmark from '/src/assets/Quoll-Wordmark.svg';
import AddressSearch from '/src/Components/AddressSearch/AddressSearch';
import { CircleUser, Menu, X } from 'lucide-react';
import { AddressSearchContext, WindowSizeContext } from '/src/App';
import GetPageRoutes from '/src/services/Content/GetPageRoutes';
import './style.css';

const PageHeader = () => {

    const { handleLogout } = useContext(AddressSearchContext);
    const { deviceType } = useContext(WindowSizeContext);
    const navigate = useNavigate();
    const location = useLocation();
    const isHomePage = location.pathname === '/';
    const [isHeroVisible, setIsHeroVisible] = useState(true);
    const [showDropdown, setShowDropdown] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isHeroSectionAddressSearchVisible, setIsHeroSectionAddressSearchVisible] = useState(true);

    const pageRoutes = GetPageRoutes();
    const headerRoutes = pageRoutes.header;
    const footerRoutes = pageRoutes.footer;

    // Check the saved user in local storage and if they are logged in
    const savedUser = localStorage.getItem('user');
    const user = savedUser ? JSON.parse(savedUser) : null;
    const isLoggedIn = user && !user.isAnonymous;

    console.log('isHeroSectionAddressSearchVisible', isHeroSectionAddressSearchVisible);
    /**
     * Sets up an IntersectionObserver to track visibility of the
     * hero-section-address-search-container. Sets isHeroSectionAddressSearchVisible
     * to true if visible, false if not.
     */
    useEffect(() => {
        if (!isHomePage) return;

        let intersectionObserver = null;
        let mutationObserver = null;

        const setupIntersectionObserver = (element) => {
            intersectionObserver = new window.IntersectionObserver(
                ([entry]) => {
                    setIsHeroSectionAddressSearchVisible(entry.isIntersecting);
                },
                { threshold: [0.1] }
            );
            intersectionObserver.observe(element);
        };

        const element = document.getElementById('hero-section-address-search-container');

        if (element) {
            setupIntersectionObserver(element);
        } else {
            // Element not yet in DOM — wait for it to be added
            mutationObserver = new MutationObserver(() => {
                const el = document.getElementById('hero-section-address-search-container');
                if (el) {
                    mutationObserver.disconnect();
                    mutationObserver = null;
                    setupIntersectionObserver(el);
                }
            });
            mutationObserver.observe(document.body, { childList: true, subtree: true });
        }

        return () => {
            intersectionObserver?.disconnect();
            mutationObserver?.disconnect();
        };
    }, [isHomePage]);

    useEffect(() => {
        if (isHomePage) {
            // console.log('Setting up observer');

            const observer = new IntersectionObserver(
                ([entry]) => {
                    // console.log('Hero section visibility:', entry.isIntersecting);
                    setIsHeroVisible(entry.isIntersecting);
                },
                {
                    threshold: [0.2],  // When 10% of the hero is visible
                    rootMargin: '-80px 0px 0px 0px' // Account for header height
                }
            );

            const heroSection = document.querySelector('.hero-section');
            // console.log('Hero section found:', heroSection);
            if (heroSection) {
                observer.observe(heroSection);
                // console.log('Now observing hero section');
            }

            return () => {
                // console.log('Cleaning up observer');
                observer.disconnect();
            };
        }
    }, [isHomePage]);



    const handleLogoClick = () => {
        if (window.location.pathname !== '/') {
            navigate('/');
        }
    }

    const handleRouteClick = (path) => {
        navigate(path);
    }

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    // Add click outside handler for mobile menu
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isMobileMenuOpen && !event.target.closest('.page-header-mobile-menu')) {
                setIsMobileMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isMobileMenuOpen]);

    // Render mobile hamberger icon and dropdown menu
    const renderMobileMenu = () => {
        return (
            <>
                <button
                    className="page-header-mobile-toggle"
                    onClick={toggleMobileMenu}
                    aria-label="Toggle menu"
                >
                    {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                </button>

                {isMobileMenuOpen && (
                    <div className="page-header-mobile-menu">
                        <div className="page-header-mobile-routes">
                            {headerRoutes.map((route, index) => (
                                <div key={route.path || `route-${index}`} className="page-header-mobile-route-with-subroutes">
                                    {!route.subRoutes ? (
                                        <Link
                                            key={`${route.path}-link`}
                                            to={route.path}
                                            className="page-header-mobile-route-button"
                                            onClick={() => setIsMobileMenuOpen(false)}
                                        >
                                            {route.label}
                                        </Link>
                                    ) : (
                                        <>
                                            <div className="page-header-mobile-route-label" key={route.label}>
                                                {route.label}
                                            </div>
                                            <div className="page-header-mobile-subroutes" key={`${route.label}-subroutes`}>
                                                {route.subRoutes.map((subRoute) => (
                                                    <Link
                                                        key={subRoute.path}
                                                        to={subRoute.path}
                                                        className="page-header-mobile-subroute-button"
                                                        onClick={() => setIsMobileMenuOpen(false)}
                                                    >
                                                        {subRoute.label}
                                                    </Link>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}

                            {isLoggedIn ? (
                                <button
                                    className="page-header-mobile-route-button"
                                    onClick={() => {
                                        handleLogout();
                                        setIsMobileMenuOpen(false);
                                    }}
                                >
                                    Sign out
                                </button>
                            ) : (
                                <Link
                                    to="/sign-in"
                                    className="page-header-mobile-route-button"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                >
                                    Sign In
                                </Link>
                            )}
                        </div>
                    </div>
                )}
            </>
        );
    }

    const renderDesktopMenu = () => {
        return (
            <div className={`page-header-routes-container ${deviceType} ${(isHomePage && isHeroSectionAddressSearchVisible) ? 'home-page-no-address-search' : ''}`}>
                {headerRoutes.map((route, index) => (
                    <div
                        key={route.path || `route-${index}`}
                        className={`
                                page-header-route 
                                text-align-center
                                ${isHomePage ? 'page-header-route-home' : ''}
                                ${route.subRoutes ? 'page-header-route-with-dropdown' : ''}
                            `}
                    >
                        {!route.subRoutes ? (
                            <Link
                                to={route.path}
                                className={`page-header-route-label ${isHomePage ? 'page-header-route-label-home' : ''}`}
                            >
                                {route.label}
                            </Link>
                        ) : (
                            <span
                                className={`page-header-route-label ${isHomePage ? 'page-header-route-label-home' : ''}`}
                            >
                                {route.label}
                            </span>
                        )}

                        {route.subRoutes && (
                            <div className="page-header-dropdown">
                                {route.subRoutes.map((subRoute) => (
                                    <Link
                                        key={subRoute.path}
                                        to={subRoute.path}
                                        className="page-header-dropdown-item"
                                    >
                                        {subRoute.label}
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>
                ))}

                {/* If the user is logged in, show the user icon */}
                {/* If the user is no logged in, show the sign in link */}
                {isLoggedIn ?
                    (
                        <div
                            className="user-menu-container"
                            onMouseEnter={() => setShowDropdown(true)}
                            onMouseLeave={() => setShowDropdown(false)}
                        >
                            <CircleUser className="page-header-user-icon page-header-route page-header-route-home" />
                            {showDropdown && (
                                <div className="user-dropdown">
                                    <button className="user-dropdown-item" onClick={() => handleLogout()}>
                                        Sign out
                                    </button>
                                </div>
                            )}
                        </div>
                    ) :
                    (
                        <div
                            className={`page-header-route text-align-center ${isHomePage ? 'page-header-route-home' : ''}`}
                            onClick={() => handleRouteClick('/sign-in')}
                        >
                            <span className={`page-header-route-label text-align-center ${isHomePage ? 'page-header-route-label-home' : ''}`}>
                                Sign In
                            </span>
                        </div>
                    )

                }
            </div>
        )
    }

    const renderQuollWordmark = () => {
        return (
            <div className="page-header-logo-container">
                <img src={QuollWordmark} alt="Quoll Intelligence Logo" className="page-header-logo" onClick={handleLogoClick} />
            </div>
        )
    }

    return (
        <div className={`
            page-header-container 
            ${isHomePage ? 'page-header-home' : ''} 
            ${!isHeroVisible ? 'page-header-scrolled' : ''}
            ${deviceType}
        `}>
            {(deviceType === 'mobile' || deviceType === 'tablet') && (
                <>
                    {renderQuollWordmark()}
                    {/* <AddressSearch locationType="header" /> */}
                    {(!isHomePage || !isHeroSectionAddressSearchVisible) && (
                        <AddressSearch locationType="header" />
                    )}
                    {renderMobileMenu()}
                </>
            )}
            {deviceType === 'desktop' && (
                <>
                    <div className={`page-header-desktop-logo-routes-container ${deviceType} ${(isHomePage && isHeroSectionAddressSearchVisible) ? 'home-page-no-address-search' : ''}`}>
                        {renderQuollWordmark()}
                        {renderDesktopMenu()}
                    </div>
                    {(!isHomePage || !isHeroSectionAddressSearchVisible) && (
                        <AddressSearch locationType="header" />
                    )}
                </>
            )}
            {/* {renderQuollWordmark()}
            {((!isHomePage || (!isHeroVisible && isHomePage)) && deviceType === 'mobile') && <AddressSearch locationType="header" />}

            {deviceType === 'mobile' ? (
                renderMobileMenu()
            ) : (
                renderDesktopMenu()
            )}
            {((!isHomePage || (!isHeroVisible && isHomePage)) && deviceType !== 'mobile') && <AddressSearch locationType="header" />} */}

        </div>
    );
};

export default PageHeader;